import {
  Box,
  Button,
  Collapse,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { compact } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { FiChevronDown, FiChevronRight, FiFilter } from "react-icons/fi";
import type { FilterToggle } from "./fhir-section";

interface DropdownFilterProps {
  filters: FilterToggle[];
  activeFilters: FilterToggle[];
  setActiveFilters: (filters: FilterToggle[]) => void;
}

export default function DropdownFilter({
  filters,
  activeFilters,
  setActiveFilters,
}: DropdownFilterProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  const toggleGroup = useCallback((groupLabel: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupLabel)) {
        newSet.delete(groupLabel);
      } else {
        newSet.add(groupLabel);
      }
      return newSet;
    });
  }, []);

  // Group filters by groupLabel
  const groupOptions = useMemo(() => {
    const groups: Record<string, FilterToggle[]> = {};

    filters.forEach(filter => {
      if (!filter.groupLabel) return;

      if (!groups[filter.groupLabel]) {
        groups[filter.groupLabel] = [];
      }

      groups[filter.groupLabel]?.push(filter);
    });

    return groups;
  }, [filters]);

  // Get current selections from active filters
  const currentSelections = useMemo(() => {
    const selections: Record<string, string[]> = {};
    Object.keys(groupOptions).forEach(group => {
      const activeFiltersForGroup = activeFilters.filter(af => af.groupLabel === group);
      selections[group] = activeFiltersForGroup.map(af => af.label);
    });
    return selections;
  }, [activeFilters, groupOptions]);

  const handleMenuChange = useCallback(
    (groupLabel: string, values: string | string[]) => {
      const selectedValues = Array.isArray(values) ? values : [values];

      // Remove all filters for this group
      const withoutGroup = activeFilters.filter(af => af.groupLabel !== groupLabel);

      // Add back the selected filters
      const groupFilters = filters.filter(f => f.groupLabel === groupLabel);
      const newActiveFilters = selectedValues
        .map(value => groupFilters.find(f => f.label === value))
        .filter((f): f is FilterToggle => f !== undefined)
        .map(f => ({ ...f, isChecked: true }));

      setActiveFilters([...withoutGroup, ...newActiveFilters]);
    },
    [activeFilters, setActiveFilters, filters]
  );

  // Create active filter tags for display
  const activeFilterTags = useMemo(
    () =>
      compact(
        Object.entries(currentSelections).flatMap(([group, values]) =>
          values.map(value => ({
            key: `${group}-${value}`,
            label: value,
            onClear: () => {
              const remainingValues = values.filter(v => v !== value);
              handleMenuChange(group, remainingValues);
            },
          }))
        )
      ),
    [currentSelections, handleMenuChange]
  );

  const menuContent = useMemo(() => {
    const groupEntries = Object.entries(groupOptions);

    return groupEntries.map(([groupLabel, filterOptions], index) => {
      const isExpanded = expandedGroups.has(groupLabel);

      return (
        <Box key={groupLabel}>
          <Flex
            align="center"
            justify="space-between"
            px={3}
            py={2}
            cursor="pointer"
            _hover={{ bg: hoverBg }}
            onClick={() => toggleGroup(groupLabel)}
          >
            <Text fontWeight="bold" fontSize="sm">
              {groupLabel}
            </Text>
            <IconButton
              aria-label={isExpanded ? "Collapse" : "Expand"}
              icon={isExpanded ? <FiChevronDown /> : <FiChevronRight />}
              size="xs"
              variant="ghost"
              onClick={e => {
                e.stopPropagation();
                toggleGroup(groupLabel);
              }}
            />
          </Flex>
          <Collapse in={isExpanded}>
            <MenuOptionGroup
              type="checkbox"
              value={currentSelections[groupLabel] || []}
              onChange={values => handleMenuChange(groupLabel, values)}
            >
              {filterOptions.map((filter: FilterToggle) => (
                <MenuItemOption key={filter.label} value={filter.label}>
                  {filter.label}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
          </Collapse>
          {index < groupEntries.length - 1 && <MenuDivider />}
        </Box>
      );
    });
  }, [groupOptions, currentSelections, handleMenuChange, expandedGroups, hoverBg, toggleGroup]);

  return (
    <Flex w="auto" align="center" gap={2} direction={{ base: "column", md: "row" }}>
      <Flex gap={2} flexWrap="wrap">
        {activeFilterTags.map(f => (
          <Tag key={f.key} size="sm" colorScheme="blue" variant="subtle" borderRadius="md">
            <TagLabel>{f.label}</TagLabel>
            <TagCloseButton onClick={f.onClear} />
          </Tag>
        ))}
      </Flex>
      <Menu closeOnSelect={false} placement="bottom-end">
        <MenuButton
          as={Button}
          variant="outline"
          leftIcon={<FiFilter />}
          borderColor={borderColor}
          bg={bg}
          _hover={{ bg: hoverBg }}
          size="sm"
        >
          Edit Filters
        </MenuButton>
        <MenuList minWidth="300px" bg={bg} borderColor={borderColor} zIndex={3}>
          <Button size="sm" onClick={() => setActiveFilters([])} m={2}>
            Reset All
          </Button>
          <MenuDivider />
          {menuContent}
        </MenuList>
      </Menu>
    </Flex>
  );
}
